import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import { useAuth, formatApiError } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Leaf } from "@phosphor-icons/react";
import { motion } from "framer-motion";

export default function Login() {
  const { t } = useI18n();
  const { login, verify2fa, api } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(identifier, password);
      if (res && res.twofa_required) { setPending(res); setLoading(false); return; }
      navigate(res.role === "client" ? "/dashboard" : "/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await verify2fa(pending.pending_token, code.trim());
      navigate(u.role === "client" ? "/dashboard" : "/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      const r = await api.post("/auth/2fa/resend", { pending_token: pending.pending_token });
      setPending({ ...pending, hint: r.data.hint });
      toast.success(t("twofa_resent"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-emerald-900 grain text-white p-12">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/logo.png" className="h-11 w-11 rounded-lg bg-white p-1 object-contain" alt="logo" />
          <span className="font-display font-extrabold text-xl">{t("brand")}</span>
        </Link>
        <div>
          <Leaf size={48} weight="fill" className="text-emerald-400 mb-6" />
          <h2 className="font-display text-4xl font-bold leading-tight">{t("hero_title")}</h2>
          <p className="mt-4 text-emerald-200 max-w-md">{t("hero_sub")}</p>
        </div>
        <div className="text-emerald-400 text-sm">© {new Date().getFullYear()} {t("brand")}</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F8FAFC]">
        <motion.form
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          onSubmit={pending ? verify : submit} className="w-full max-w-md" data-testid="login-form"
        >
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/logo.png" className="h-9 w-9" alt="logo" />
            <span className="font-display font-extrabold text-emerald-900 text-lg">{t("brand")}</span>
          </Link>
          <h1 className="font-display text-3xl font-bold text-emerald-900">{t("welcome_back")}</h1>
          <p className="text-slate-500 mt-2 mb-8">{t("login_sub")}</p>

          {error && <div data-testid="login-error" className="mb-4 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{error}</div>}

          {!pending ? (
          <>
          <div className="space-y-4">
            <div>
              <Label className="text-emerald-900 font-semibold">{t("email_or_phone")}</Label>
              <Input data-testid="login-identifier-input" className="mt-1.5 h-11" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required />
            </div>
            <div>
              <Label className="text-emerald-900 font-semibold">{t("password")}</Label>
              <Input data-testid="login-password-input" type="password" className="mt-1.5 h-11" value={password} onChange={(e) => setPassword(e.target.value)} required />
              <div className="text-right mt-1.5">
                <Link to="/forgot-password" data-testid="forgot-link" className="text-sm text-emerald-700 font-semibold hover:underline">{t("forgot_password")}</Link>
              </div>
            </div>
          </div>

          <Button data-testid="login-submit-btn" disabled={loading} className="w-full mt-6 h-11 bg-emerald-800 hover:bg-emerald-900 text-base">
            {loading ? t("loading") : t("login")}
          </Button>

          <p className="text-center text-sm text-slate-500 mt-6">
            {t("no_account")}{" "}
            <Link to="/register" data-testid="go-register-link" className="text-emerald-700 font-semibold hover:underline">{t("register")}</Link>
          </p>
          </>
          ) : (
          <>
            <p className="text-slate-500 mb-4 text-sm">{t("twofa_prompt")} <b className="text-emerald-800">{pending.hint}</b></p>
            <Input data-testid="twofa-code-input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="000000" className="h-12 text-center tracking-[0.5em] text-xl" />
            <Button data-testid="twofa-verify-btn" disabled={loading || code.length < 6} className="w-full mt-4 h-11 bg-emerald-800 hover:bg-emerald-900 text-base">
              {loading ? t("loading") : t("twofa_verify")}
            </Button>
            <button type="button" data-testid="twofa-resend-btn" onClick={resend} className="block mx-auto mt-4 text-sm text-emerald-700 font-semibold hover:underline">{t("twofa_resend")}</button>
          </>
          )}
        </motion.form>
      </div>
    </div>
  );
}
